// Transactions.js
import React, { useContext, useEffect, useState, useMemo, useCallback } from "react";
import * as XLSX from "xlsx";
import TransactionsBox from "./TransactionsBox";
import ButtonBar from "../../Elements/ButtonBar";
import { UserContext } from "../../../context/UserContext";
import { formatApiDateTime, parseApiDateTime } from "../../../lib/helpers";

export default function Transactions() {
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [token, , , venueID] = useContext(UserContext);
  
  // Filtros
  const [searchInput, setSearchInput] = useState("");
  const [filterType, setFilterType] = useState("all"); // all, sale, credit, refund
  const [filterDate, setFilterDate] = useState("all"); // all, today, week, month
  const [draftFromDate, setDraftFromDate] = useState("");
  const [draftToDate, setDraftToDate] = useState("");
  const [customFromDate, setCustomFromDate] = useState("");
  const [customToDate, setCustomToDate] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  const getTransactionDate = useCallback((tx) => {
    const rawDate = tx.timestamp || tx.created_at;
    const parsedDate = rawDate ? parseApiDateTime(rawDate) : null;
    return parsedDate && !Number.isNaN(parsedDate.getTime()) ? parsedDate : null;
  }, []);

  const getTransactionTypeLabel = useCallback((type) => {
    const normalizedType = type?.toLowerCase();
    if (normalizedType === "purchase" || normalizedType === "sale" || normalizedType === "venta") {
      return "Venta";
    }
    if (
      normalizedType === "credit" ||
      normalizedType === "credito" ||
      normalizedType === "crédito" ||
      normalizedType === "add_cash"
    ) {
      return "Crédito";
    }
    if (normalizedType === "refund" || normalizedType === "reembolso") {
      return "Reembolso";
    }
    return type || "-";
  }, []);

  const formatDateForExport = useCallback(
    (value) => formatApiDateTime(value, { fallback: "-" }),
    []
  );

  const buildDateBoundary = useCallback((value, boundary) => {
    if (!value) return null;
    const parsed = new Date(`${value}T00:00:00`);
    if (Number.isNaN(parsed.getTime())) return null;
    if (boundary === "end") {
      parsed.setHours(23, 59, 59, 999);
    }
    return parsed;
  }, []);

  const applyCustomDateRange = useCallback(() => {
    const fromBoundary = buildDateBoundary(draftFromDate, "start");
    const toBoundary = buildDateBoundary(draftToDate, "end");

    if (fromBoundary && toBoundary && fromBoundary > toBoundary) {
      setErrorMessage("La fecha 'Desde' no puede ser mayor que 'Hasta'.");
      return;
    }

    setErrorMessage("");
    setCustomFromDate(draftFromDate);
    setCustomToDate(draftToDate);
    setFilterDate("all");
  }, [buildDateBoundary, draftFromDate, draftToDate]);

  const clearCustomDateRange = useCallback(() => {
    setDraftFromDate("");
    setDraftToDate("");
    setCustomFromDate("");
    setCustomToDate("");
    setErrorMessage("");
  }, []);

  const fetchTransactions = useCallback(async () => {
    if (!token) {
      setErrorMessage('No hay token de autenticación');
      setLoaded(true);
      return;
    }
    
    setLoaded(false);
    setErrorMessage("");
    
    try {
      // Construir URL con parámetro venue_id si está disponible
      let url = "/v1/get-all-transactions";
      if (venueID) {
        url += `?venue_id=${venueID}`;
      }
      
      const res = await fetch(url, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({ detail: 'Error al obtener transacciones' }));
        throw new Error(errorData.detail || "No tienes privilegios para ver esto");
      }
      
      const data = await res.json();
      let txList = Array.isArray(data.transactions) ? data.transactions : (Array.isArray(data) ? data : []);
      // No incluir operaciones anuladas (reversed) en la lista de Transacciones
      txList = txList.filter(tx => (tx.status || '').toLowerCase() !== 'reversed');
      setTransactions(txList);
      setErrorMessage("");
    } catch (err) {
      console.error("Failed to fetch transactions:", err);
      setErrorMessage(err.message || "Error al cargar transacciones");
      setTransactions([]);
    } finally {
      setLoaded(true);
    }
  }, [token, venueID]); // Dependencias: token y venueID

  // Anular una venta: devuelve stock + saldo y la oculta de la lista (status reversed).
  const handleReverse = useCallback(async (transaction) => {
    if (!transaction?.id) return;
    const ok = window.confirm(
      "¿Anular esta venta? Se devolverá el stock y, si fue a crédito, el saldo al socio."
    );
    if (!ok) return;
    try {
      const res = await fetch(`/v1/reverse-purchase/${transaction.id}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          ...(venueID && { "x-venue-id": venueID }),
        },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "No se pudo anular la venta");
      await fetchTransactions();
    } catch (err) {
      setErrorMessage(err.message || "No se pudo anular la venta");
    }
  }, [token, venueID, fetchTransactions]);

  const exportFilteredTransactions = useCallback(() => {
    if (filteredTransactions.length === 0) {
      setErrorMessage("No hay transacciones filtradas para exportar.");
      return;
    }

    setErrorMessage("");

    const rows = filteredTransactions.map((tx) => {
      const items = Array.isArray(tx.items) ? tx.items : [];
      const productsSummary = items.length > 0
        ? items
            .map((item) => {
              const productName = item.product_name || `Producto #${item.product_id || "-"}`;
              const quantity = Number(item.quantity || 0);
              const unitPrice = Number(item.unit_price || 0);
              const lineTotal = quantity * unitPrice;
              return `${productName} x${quantity} (${unitPrice.toFixed(2)} EUR) = ${lineTotal.toFixed(2)} EUR`;
            })
            .join(" | ")
        : "Sin items";

      return {
        ID: tx.id || "-",
        Usuario: tx.user_name || "-",
        Email: tx.user_email || "-",
        "Atendido por": tx.processed_by_name || "-",
        "ID usuario": tx.user_id || "-",
        Tienda: tx.shop_name || "-",
        "ID tienda": tx.shop_id || "-",
        Tipo: getTransactionTypeLabel(tx.type),
        Estado: tx.status || "-",
        Monto: Number(tx.amount || 0),
        Fecha: formatDateForExport(tx.timestamp || tx.created_at),
        Productos: productsSummary,
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Transacciones");

    const exportDate = new Date().toISOString().slice(0, 10);
    const filename = `transacciones-filtradas-${exportDate}.xlsx`;
    XLSX.writeFile(workbook, filename);
  }, [filteredTransactions, formatDateForExport, getTransactionTypeLabel]);

  useEffect(() => {
    if (token) fetchTransactions();
  }, [token, venueID, fetchTransactions]); // Recargar cuando cambie el token o venueID

  // Filtrado en tiempo real
  useEffect(() => {
    let filtered = [...transactions];

    // No mostrar operaciones anuladas (ej. add_cash deshecho)
    filtered = filtered.filter(tx => (tx.status || '').toLowerCase() !== 'reversed');

    // Filtrar por búsqueda
    if (searchInput && searchInput.trim() !== "") {
      const searchTerm = searchInput.toLowerCase();
      filtered = filtered.filter(tx => {
        // Solo campos con sentido para el staff. NO se busca contra UUIDs
        // (user_id/shop_id): al ser hex, un término numérico como "14" casaba con
        // UUIDs al azar y salían transacciones que no tocaban (bug del filtro).
        const txId = (tx.id?.toString() || '').slice(0, 8).toLowerCase(); // ID corto mostrado (#xxxxxxxx)
        const userName = (tx.user_name || '').toLowerCase();
        const userEmail = (tx.user_email || '').toLowerCase();
        const shopName = (tx.shop_name || '').toLowerCase();
        const amount = tx.amount?.toString() || '';
        // Nº socio (visible = legacy || interno) para poder buscar por número, p.ej. "14"
        const membershipId = tx.membership_id != null ? String(tx.membership_id) : '';
        const legacyMemberId = (tx.legacy_member_id ? String(tx.legacy_member_id) : '').toLowerCase();

        return (
          txId.includes(searchTerm) ||
          userName.includes(searchTerm) ||
          userEmail.includes(searchTerm) ||
          shopName.includes(searchTerm) ||
          amount.includes(searchTerm) ||
          membershipId.includes(searchTerm) ||
          legacyMemberId.includes(searchTerm)
        );
      });
    }

    // Filtrar por tipo
    if (filterType !== "all") {
      filtered = filtered.filter(tx => {
        const txType = tx.type?.toLowerCase();
        // Mapear términos en español e inglés, incluyendo add_cash y purchase
        if (filterType === "venta" && (txType === "purchase" || txType === "sale" || txType === "venta")) return true;
        if (filterType === "credito" && (txType === "credit" || txType === "credito" || txType === "crédito" || txType === "add_cash")) return true;
        if (filterType === "reembolso" && (txType === "refund" || txType === "reembolso")) return true;
        return txType === filterType;
      });
    }

    // Filtrar por fecha
    if (filterDate !== "all") {
      const now = new Date();
      filtered = filtered.filter(tx => {
        const txDate = getTransactionDate(tx);
        if (!txDate) return false;
        
        switch(filterDate) {
          case "today":
            return txDate.toDateString() === now.toDateString();
          case "week":
            const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            return txDate >= weekAgo;
          case "month":
            const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
            return txDate >= monthAgo;
          default:
            return true;
        }
      });
    }

    if (customFromDate || customToDate) {
      const fromBoundary = buildDateBoundary(customFromDate, "start");
      const toBoundary = buildDateBoundary(customToDate, "end");

      filtered = filtered.filter((tx) => {
        const txDate = getTransactionDate(tx);
        if (!txDate) return false;
        if (fromBoundary && txDate < fromBoundary) return false;
        if (toBoundary && txDate > toBoundary) return false;
        return true;
      });
    }

    // ✅ ORDENAR por fecha descendente (más recientes primero)
    filtered.sort((a, b) => {
      const dateA = getTransactionDate(a) || new Date(0);
      const dateB = getTransactionDate(b) || new Date(0);
      return dateB - dateA; // Orden descendente
    });
    setFilteredTransactions(filtered);
    setCurrentPage(1); // Reset a la primera página al filtrar
  }, [searchInput, filterType, filterDate, transactions, customFromDate, customToDate, buildDateBoundary, getTransactionDate]);

  // Estadísticas
  const stats = useMemo(() => {
    const total = filteredTransactions.reduce((sum, tx) => sum + (parseFloat(tx.amount) || 0), 0);
    const sales = filteredTransactions.filter(tx => {
      const type = tx.type?.toLowerCase();
      return type === 'purchase' || type === 'sale' || type === 'venta';
    }).length;
    const credits = filteredTransactions.filter(tx => {
      const type = tx.type?.toLowerCase();
      return type === 'credit' || type === 'credito' || type === 'crédito' || type === 'add_cash';
    }).length;
    const refunds = filteredTransactions.filter(tx => {
      const type = tx.type?.toLowerCase();
      return type === 'refund' || type === 'reembolso';
    }).length;
    
    return {
      total: total.toFixed(2),
      count: filteredTransactions.length,
      sales,
      credits,
      refunds
    };
  }, [filteredTransactions]);

  // Paginación
  const totalPages = Math.ceil(filteredTransactions.length / itemsPerPage);
  const paginatedTransactions = filteredTransactions.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const menuBar = {
    title: "Transacciones",
    buttons: [
      { label: "Exportar Excel", color: "green", onClick: exportFilteredTransactions },
      { label: "Recargar", color: "blue", onClick: fetchTransactions },
    ],
  };

  return (
    <div className="p-2 sm:p-4 space-y-3 sm:space-y-4">
      {/* Header */}
      {/* Desktop: barra normal */}
      <div className="hidden md:block">
        <ButtonBar
          title={menuBar.title}
          buttons={menuBar.buttons}
        />
      </div>
      {/* Móvil: stats (Total, Trans, Ventas, Créd, Reemb) como botones alargados */}
      <div className="block md:hidden bg-white p-4 rounded-lg shadow border border-gray-200 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-800">
            Transacciones
          </h2>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={exportFilteredTransactions}
              className="px-3 py-1.5 bg-green-500 hover:bg-green-600 text-white text-xs font-semibold rounded"
            >
              Excel
            </button>
            <button
              type="button"
              onClick={fetchTransactions}
              className="px-3 py-1.5 bg-blue-500 hover:bg-blue-600 text-white text-xs font-semibold rounded"
            >
              Recargar
            </button>
          </div>
        </div>
        {loaded && filteredTransactions.length > 0 && (
          <div className="space-y-2">
            <div className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg bg-blue-50 border border-blue-200">
              <span className="text-xs text-blue-600 font-medium">Total</span>
              <span className="text-sm font-bold text-blue-900">€{stats.total}</span>
            </div>
            <div className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg bg-gray-50 border border-gray-200">
              <span className="text-xs text-gray-600 font-medium">Trans</span>
              <span className="text-sm font-bold text-gray-900">{stats.count}</span>
            </div>
            <div className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg bg-green-50 border border-green-200">
              <span className="text-xs text-green-600 font-medium">Ventas</span>
              <span className="text-sm font-bold text-green-900">{stats.sales}</span>
            </div>
            <div className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg bg-purple-50 border border-purple-200">
              <span className="text-xs text-purple-600 font-medium">Créd</span>
              <span className="text-sm font-bold text-purple-900">{stats.credits}</span>
            </div>
            <div className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg bg-red-50 border border-red-200">
              <span className="text-xs text-red-600 font-medium">Reemb</span>
              <span className="text-sm font-bold text-red-900">{stats.refunds}</span>
            </div>
          </div>
        )}
      </div>

      {/* Error */}
      {errorMessage && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
          <strong className="font-bold">Error: </strong>
          <span className="block sm:inline">{errorMessage}</span>
          <button
            className="absolute top-0 bottom-0 right-0 px-4 py-3"
            onClick={() => setErrorMessage("")}
          >
            <span className="text-red-700">×</span>
          </button>
        </div>
      )}

      {/* Estadísticas (solo desktop / tablet grande) */}
      {loaded && filteredTransactions.length > 0 && (
        <div className="hidden md:flex flex-wrap gap-2">
          <div className="bg-blue-50 border border-blue-200 rounded px-2 py-1 flex items-center gap-1.5">
            <span className="text-xs text-blue-600 font-medium whitespace-nowrap">Total:</span>
            <span className="text-sm font-bold text-blue-900 min-w-[4rem]">€{stats.total}</span>
          </div>
          <div className="bg-gray-50 border border-gray-200 rounded px-2 py-1 flex items-center gap-1.5">
            <span className="text-xs text-gray-600 font-medium whitespace-nowrap">Trans:</span>
            <span className="text-sm font-bold text-gray-900 min-w-[3rem]">{stats.count}</span>
          </div>
          <div className="bg-green-50 border border-green-200 rounded px-2 py-1 flex items-center gap-1.5">
            <span className="text-xs text-green-600 font-medium whitespace-nowrap">Ventas:</span>
            <span className="text-sm font-bold text-green-900 min-w-[3rem]">{stats.sales}</span>
          </div>
          <div className="bg-purple-50 border border-purple-200 rounded px-2 py-1 flex items-center gap-1.5">
            <span className="text-xs text-purple-600 font-medium whitespace-nowrap">Créd:</span>
            <span className="text-sm font-bold text-purple-900 min-w-[3rem]">{stats.credits}</span>
          </div>
          <div className="bg-red-50 border border-red-200 rounded px-2 py-1 flex items-center gap-1.5">
            <span className="text-xs text-red-600 font-medium whitespace-nowrap">Reemb:</span>
            <span className="text-sm font-bold text-red-900 min-w-[3rem]">{stats.refunds}</span>
          </div>
        </div>
      )}

      {/* Filtros */}
      <div className="bg-white p-3 md:p-4 rounded-lg shadow border border-gray-200 space-y-3">
        {/* Búsqueda */}
        <div>
          <input
            className="text-sm focus:outline-none active:outline-none border border-gray-300 w-full h-10 px-4 rounded"
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Buscar por: ID, Nombre, Email, Tienda, Monto..."
            value={searchInput}
          />
        </div>

        {/* Filtros de tipo y fecha */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
            <select
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
            >
              <option value="all">Todas</option>
              <option value="venta">Ventas</option>
              <option value="credito">Créditos</option>
              <option value="reembolso">Reembolsos</option>
            </select>
          </div>

          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Período</label>
            <select
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={filterDate}
              onChange={(e) => {
                setFilterDate(e.target.value);
                setDraftFromDate("");
                setDraftToDate("");
                setCustomFromDate("");
                setCustomToDate("");
              }}
            >
              <option value="all">Todas las fechas</option>
              <option value="today">Hoy</option>
              <option value="week">Última semana</option>
              <option value="month">Último mes</option>
            </select>
          </div>
        </div>

        <div className="border-t border-gray-100 pt-3">
          <div className="flex flex-col lg:flex-row lg:items-end gap-3">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">Desde</label>
              <input
                type="date"
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-lime-500"
                value={draftFromDate}
                onChange={(e) => setDraftFromDate(e.target.value)}
              />
            </div>

            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">Hasta</label>
              <input
                type="date"
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-lime-500"
                value={draftToDate}
                onChange={(e) => setDraftToDate(e.target.value)}
              />
            </div>

            <div className="flex gap-2 lg:pb-[1px]">
              <button
                type="button"
                onClick={applyCustomDateRange}
                className="flex-1 lg:flex-none px-4 py-2 bg-lime-500 hover:bg-lime-600 text-white text-sm font-semibold rounded transition-colors"
              >
                Filtrar
              </button>
              <button
                type="button"
                onClick={clearCustomDateRange}
                className="flex-1 lg:flex-none px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded transition-colors"
              >
                Limpiar
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Loading */}
      {!loaded && (
        <div className="flex items-center justify-center p-8">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-2 text-gray-600">Cargando transacciones...</p>
          </div>
        </div>
      )}

      {/* Transactions list */}
      {loaded && (
        <>
          <TransactionsBox transactions={paginatedTransactions} onReverse={handleReverse} />
          
          {/* Paginación */}
          {totalPages > 1 && (
            <div className="bg-white p-3 md:p-4 rounded-lg shadow border border-gray-200">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
                <div className="text-xs sm:text-sm text-gray-600 text-center sm:text-left">
                  Mostrando {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, filteredTransactions.length)} de {filteredTransactions.length}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1.5 text-sm bg-blue-500 text-white rounded disabled:bg-gray-300 disabled:cursor-not-allowed hover:bg-blue-600"
                  >
                    Anterior
                  </button>
                  <span className="px-2 py-1.5 text-xs sm:text-sm text-gray-700 whitespace-nowrap">
                    {currentPage} / {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1.5 text-sm bg-blue-500 text-white rounded disabled:bg-gray-300 disabled:cursor-not-allowed hover:bg-blue-600"
                  >
                    Siguiente
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
